import { appRoutes, AppRoute, PaidOnlyTag } from '@components';
import { useDemoGuard } from '@hooks';
import { isAnyGranted, isFeatureEnabled } from '@lib';
import { useStore } from '@stores';
import { Badge, Menu, Space, Tag } from 'antd';
import { observer } from 'mobx-react';
import { useLocation, useNavigate } from 'react-router-dom';

declare const abp: any;

interface ISiderMenuProps {
  onItemClick?: () => void;
}

export const SiderMenu = observer(({ onItemClick }: ISiderMenuProps = {}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { sessionStore } = useStore();

  const { isDemo, showDemoNotice } = useDemoGuard();

  const generateMenuItems = (routes: AppRoute[]) =>
    routes
      .filter((route) => !(route.path === '/calendar' && abp.session.impersonatorUserId !== null))
      .filter((route) => route.showInMenu && (!route.permission || isAnyGranted(route.permission)))
      .filter((route) => !route.feature || isFeatureEnabled(route.feature))
      .map((route) => {
        // Shown rather than hidden, so a prospect can see what the full account includes.
        const lockedForDemo = !!route.paidOnly && isDemo;
        const disabled = !!route.disabled;

        const item: any = {
          key: route.path,
          icon:
            route.path === '/chat' ? (
              <Space size="middle">
                <Badge size="small" count={sessionStore.unreadCount}>
                  {route.icon}
                </Badge>
              </Space>
            ) : (
              route.icon
            ),
          // Tag pushed to the trailing edge so entries stay aligned. A long title like
          // "Online booking setup" would otherwise take the whole row and push the tag out
          // of the sidebar, so the title truncates and the tag never shrinks — it is the
          // part that has to survive. Dimming sits on the title alone for the same reason.
          label: lockedForDemo || route.tag ? (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                minWidth: 0,
              }}
            >
              <span
                title={route.title}
                style={{
                  opacity: 0.75,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {route.title}
              </span>
              {route.tag ? (
                <Tag color="blue" style={{ flexShrink: 0, margin: 0 }}>
                  {route.tag}
                </Tag>
              ) : (
                <PaidOnlyTag style={{ flexShrink: 0 }} />
              )}
            </span>
          ) : (
            route.title
          ),
          onClick: () => {
            if (disabled) return;

            if (lockedForDemo) {
              showDemoNotice();
              return;
            }

            if (!route.children && route.path) {
              navigate(route.path);
              onItemClick?.();
            }
          },
        };

        if (route.children) {
          item.children = generateMenuItems(route.children);

          // A group left with one page (Jobs, for a technician, keeps only Regular): a submenu with a
          // single entry is a click for nothing, so the group becomes that entry.
          if (item.children.length === 1) {
            const [onlyChild] = item.children;
            return { ...onlyChild, icon: item.icon, label: item.label };
          }
        }

        return item;
      });

  return (
    <Menu
      theme="light"
      mode="inline"
      style={{ border: 'none' }}
      selectedKeys={[pathname]}
      items={generateMenuItems(appRoutes)}
    />
  );
});
